// Stage 6A — broadcast compose autosave decision + status logic. Pure helpers
// so the (untested) BroadcastCompose component holds only the debounce timer
// glue; everything worth asserting lives here. See ux-debt.md §"Stage 6A" —
// "Compose autosave (deferred)".

// Debounce window after the last keystroke before an autosave fires. Long
// enough not to thrash the API mid-sentence, short enough that a crash loses
// little.
export const AUTOSAVE_DEBOUNCE_MS = 2500;

export type SaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved';

// The single status pill shown next to Save draft. `hasPersisted` is true once
// a broadcast doc exists (created on the first save, or loaded in edit mode) —
// it gates the difference between a pristine brand-new form (idle, nothing to
// say) and a saved-and-clean one ("Saved").
export function computeSaveStatus(input: {
  saving: boolean;
  isDirty: boolean;
  hasPersisted: boolean;
}): SaveStatus {
  if (input.saving) return 'saving';
  if (input.isDirty) return 'unsaved';
  if (input.hasPersisted) return 'saved';
  return 'idle';
}

export const SAVE_STATUS_LABEL: Record<SaveStatus, string | null> = {
  idle: null,
  unsaved: 'Unsaved changes',
  saving: 'Saving…',
  saved: 'Saved',
};

// Whether a debounced autosave should fire right now. Guards, in order:
//  - canSave: a title + a brand are present (so a fresh draft never
//    spam-creates an empty doc before it's meaningful — ux-debt requirement).
//  - isDirty: the current draft differs from what was last persisted.
//  - not already saving, and not in the middle of the Send-now flow (a save in
//    flight, or the confirm modal open) — autosave must never race the send.
//  - retryingFailedContent: the exact content currently on screen already
//    failed to save; don't hammer the API in a 2.5s retry loop. Editing the
//    draft (which clears this flag in the caller) re-enables autosave.
export function shouldAutosave(input: {
  canSave: boolean;
  isDirty: boolean;
  saving: boolean;
  sending: boolean;
  confirming: boolean;
  retryingFailedContent: boolean;
}): boolean {
  return (
    input.canSave &&
    input.isDirty &&
    !input.saving &&
    !input.sending &&
    !input.confirming &&
    !input.retryingFailedContent
  );
}
